import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ScrollView,
  Dimensions,
  Linking,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import {
  Settings,
  Map,
  Brain,
  Lightbulb,
  FileText,
  HelpCircle,
  LogOut,
} from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSidebarNavigation } from '../../hooks/useSidebarNavigation';
import { useLogout } from '../../hooks/useLogout';
import { navigationConfig } from '../../config/navigationConfig';

interface NavigationItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ size: number; color: string }>;
  onPress: () => void;
  isActive?: boolean;
}

interface SidebarNavigationProps {
  activeRoute?: string;
  onNavigate: (route: string) => void;
}

/**
 * SidebarNavigation - Sidebar navigation for large screens
 *
 * Features:
 * - Fixed sidebar navigation for screens ≥ 768px
 * - Clean, organized navigation items
 * - Active state indication
 * - Proper spacing and accessibility
 * - Integrated with React Navigation
 */
export function SidebarNavigation({
  activeRoute = 'home',
  onNavigate,
}: SidebarNavigationProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { navigateToRoute } = useSidebarNavigation();
  const { logout } = useLogout();
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Screen size detection - use width only for consistent responsive behavior
  useEffect(() => {
    const updateScreenSize = () => {
      const { width } = Dimensions.get('window');
      const isSmall = width < navigationConfig.breakpoints.mobile;
      setIsSmallScreen(isSmall);
    };

    updateScreenSize();
    const subscription = Dimensions.addEventListener(
      'change',
      updateScreenSize
    );
    return () => subscription?.remove();
  }, []);

  const handleNavigation = (route: string) => {
    if (route === 'aiExperience') {
      Linking.openURL('https://ai-assistant.coffeeplexity.com/');
    } else {
      navigateToRoute(route);
      onNavigate(route); // Also call the prop callback for state management
    }
  };

  // Drawer items - matches hamburger menu structure
  const discoverItems: NavigationItem[] = [
    {
      id: 'recommendations',
      title: 'Recommendations',
      icon: Lightbulb,
      onPress: () => handleNavigation('recommendations'),
      isActive: activeRoute === 'recommendations',
    },
    {
      id: 'journey',
      title: 'Journey',
      icon: Map,
      onPress: () => handleNavigation('journey'),
      isActive: activeRoute === 'journey',
    },
    {
      id: 'aiExperience',
      title: 'AI Experiments (Alpha)',
      icon: Brain,
      onPress: () => handleNavigation('aiExperience'),
      isActive: activeRoute === 'aiExperience',
    },
  ];

  const accountItems: NavigationItem[] = [
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      onPress: () => handleNavigation('settings'),
      isActive: activeRoute === 'settings',
    },
    {
      id: 'termsAndConditions',
      title: 'Terms & Conditions',
      icon: FileText,
      onPress: () => handleNavigation('termsAndConditions'),
      isActive: activeRoute === 'termsAndConditions',
    },
    {
      id: 'helpSupport',
      title: 'Help & Support',
      icon: HelpCircle,
      onPress: () => handleNavigation('helpSupport'),
      isActive: activeRoute === 'helpSupport',
    },
  ];

  const handleLogout = () => {
    logout(true); // Show confirmation dialog
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const IconComponent = item.icon;
    const isActive = item.isActive;

    return (
      <Pressable
        key={item.id}
        onPress={item.onPress}
        style={[
          styles.navigationItem,
          {
            backgroundColor: isActive
              ? theme.colors.surfaceVariant
              : 'transparent',
          },
        ]}
        accessibilityRole='button'
        accessibilityLabel={`Navigate to ${item.title}`}
        accessibilityState={{ selected: isActive }}
        testID={`sidebar-nav-${item.id.toLowerCase()}`}
      >
        <View style={styles.navigationItemContent}>
          <IconComponent
            size={20}
            color={
              isActive ? theme.colors.onSurface : theme.colors.onSurfaceVariant
            }
          />
          <Text
            style={[
              styles.navigationItemText,
              {
                color: isActive
                  ? theme.colors.onSurface
                  : theme.colors.onSurfaceVariant,
                fontWeight: isActive ? '600' : '400',
              },
            ]}
          >
            {item.title}
          </Text>
        </View>
      </Pressable>
    );
  };

  // Don't render sidebar on small screens - use drawer/bottom nav instead
  if (isSmallScreen) {
    return null;
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface,
          paddingTop: insets.top + 16,
          paddingBottom: insets.bottom + 16,
          borderRightWidth: 1,
          borderRightColor: (theme.colors.outline || '#e0e0e0') + '40',
        },
      ]}
    >
      {/* App Title */}
      <View style={styles.header}>
        <Text style={[styles.appTitle, { color: theme.colors.onSurface }]}>
          Coffeeplexity
        </Text>
      </View>

      {/* Navigation Items */}
      <ScrollView
        style={styles.navigation}
        showsVerticalScrollIndicator={false}
      >
        {/* Discover Section */}
        <View style={styles.navigationSection}>
          <Text
            style={[
              styles.sectionTitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            DISCOVER
          </Text>
          {discoverItems.map(renderNavigationItem)}
        </View>

        {/* Account & Support Section */}
        <View style={styles.navigationSection}>
          <Text
            style={[
              styles.sectionTitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            ACCOUNT & SUPPORT
          </Text>
          {accountItems.map(renderNavigationItem)}
        </View>
      </ScrollView>

      {/* Logout Button */}
      <View style={styles.footer}>
        <Pressable
          onPress={() => {
            handleLogout();
          }}
          style={({ pressed }) => [
            styles.logoutButton,
            {
              borderColor: theme.colors.error,
              opacity: pressed ? 0.7 : 1,
            },
          ]}
          accessibilityRole='button'
          accessibilityLabel='Logout'
          testID='sidebar-logout-button'
        >
          <LogOut size={18} color={theme.colors.error} />
          <Text
            style={[styles.logoutButtonText, { color: theme.colors.error }]}
          >
            Logout
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 250,
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e0e0e0',
  },
  appTitle: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  navigation: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  navigationSection: {
    marginBottom: 28,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: 2,
    opacity: 0.6,
  },
  navigationItem: {
    borderRadius: 8,
    marginVertical: 2,
    overflow: 'hidden',
  },
  navigationItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  navigationItemText: {
    fontSize: 14,
    marginLeft: 12,
    flex: 1,
  },
  sectionDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#e0e0e0',
    marginVertical: 8,
    marginHorizontal: 12,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#e0e0e0',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  logoutButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default SidebarNavigation;
